/**
 * Reports — unified P&L and trade analytics (/api/reporting).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Calendar,
  LineChart,
  Loader2,
  RefreshCw,
  Table2,
  TrendingDown,
  Gauge,
  LayoutGrid,
  Clock,
  Database,
  ShieldCheck,
} from 'lucide-react';
import { reportingApi } from '../lib/api';
import type { PerformanceSummary, ReportingStatsResponse, PatternMetrics, TimeOfDayBucket, ShadowSoakResponse } from '../types/titan';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import PerformanceAnalytics from '../components/titan/PerformanceAnalytics';
import SystemOversight from '../components/titan/SystemOversight';
import { Telescope } from 'lucide-react';

const PERIODS = [7, 14, 30, 60, 90, 180, 365] as const;

const fmt$ = (v: number) => {
  const sign = v >= 0 ? '+' : '';
  return `${sign}$${v.toFixed(2)}`;
};

const pnlClass = (v: number) =>
  v > 0 ? 'text-emerald-500' : v < 0 ? 'text-rose-500' : 'text-muted-foreground';

const SESSION_ORDER = ['pre', 'rth', 'post', 'overnight'] as const;
const SESSION_LABELS: Record<string, string> = {
  pre: 'Pre-Market',
  rth: 'Regular',
  post: 'After-Hours',
  overnight: 'Overnight',
  unknown: 'Unknown',
};

/**
 * Tier 5 (item 22): per-dial shadow would-act soak evidence — the counts
 * behind every shadow→enforce flip decision, without SSH-ing into the box.
 */
function ShadowSoakCard() {
  const [soak, setSoak] = useState<ShadowSoakResponse | null>(null);
  const [soakError, setSoakError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    reportingApi
      .shadowSoak(7)
      .then((d) => alive && setSoak(d))
      .catch((e) => alive && setSoakError(e instanceof Error ? e.message : 'Failed to load shadow soak'));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" /> Shadow soak (7d)
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {soakError ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{soakError}</p>
        ) : !soak ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : soak.dials.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No shadow would-act events recorded in the last {soak.period_days} days.
          </p>
        ) : (
          <div className="overflow-auto max-h-[320px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dial</TableHead>
                  <TableHead className="text-center">Would-acts (7d)</TableHead>
                  <TableHead className="text-center">Active days</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {soak.dials.map((d) => (
                  <TableRow key={d.dial}>
                    <TableCell className="font-mono text-xs">{d.dial}</TableCell>
                    <TableCell className="text-center font-semibold">{d.total}</TableCell>
                    <TableCell className="text-center">{Object.keys(d.days).length}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {d.last_seen ? d.last_seen.slice(0, 16).replace('T', ' ') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="text-xs text-muted-foreground px-4 py-2 border-t">
          Would-act evidence from shadow-mode dials — the soak data behind each shadow → enforce flip.
        </p>
      </CardContent>
    </Card>
  );
}

function MiniEquityChart({ points }: { points: Array<{ cumulative_pnl: number }> }) {
  if (!points.length) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No equity data yet.</p>;
  }
  const vals = points.map((p) => p.cumulative_pnl);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 1);
  const range = max - min || 1;
  const w = 800;
  const h = 200;
  const coords = vals
    .map((v, i) => {
      const x = (i / Math.max(vals.length - 1, 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-48 text-primary">
      <polyline fill="none" stroke="currentColor" strokeWidth="2" points={coords} />
    </svg>
  );
}

export default function Reports() {
  const [periodDays, setPeriodDays] = useState(30);
  const [tab, setTab] = useState('oversight');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ReportingStatsResponse | null>(null);
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [daily, setDaily] = useState<Array<Record<string, unknown>>>([]);
  const [equity, setEquity] = useState<Array<{ cumulative_pnl: number; timestamp?: string }>>([]);
  const [trades, setTrades] = useState<Array<Record<string, unknown>>>([]);
  const [patterns, setPatterns] = useState<PatternMetrics[]>([]);
  const [tod, setTod] = useState<TimeOfDayBucket[]>([]);
  const [sessions, setSessions] = useState<Array<{ session: string; metrics: Record<string, number> }>>([]);
  const [drawdown, setDrawdown] = useState<Record<string, unknown> | null>(null);
  const [audit, setAudit] = useState<Record<string, unknown> | null>(null);
  const [persisting, setPersisting] = useState(false);
  const [scrubPreview, setScrubPreview] = useState<Record<string, unknown> | null>(null);
  const [scrubbing, setScrubbing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [st, sm, dh, ec, tr, bp, td, bs, dd, au] = await Promise.allSettled([
        reportingApi.stats(),
        reportingApi.summary(periodDays),
        reportingApi.dailyHistory(periodDays),
        reportingApi.equityCurve(Math.max(periodDays, 90)),
        reportingApi.trades({ days: periodDays, limit: 200 }),
        reportingApi.byPattern(periodDays),
        reportingApi.byTimeOfDay(periodDays),
        reportingApi.bySession(periodDays),
        reportingApi.drawdown(Math.max(periodDays, 90)),
        reportingApi.exitLogAudit(365),
      ]);
      if (st.status === 'fulfilled') setStats(st.value);
      if (sm.status === 'fulfilled') setSummary(sm.value);
      if (dh.status === 'fulfilled') setDaily((dh.value.days as Array<Record<string, unknown>>) || []);
      if (ec.status === 'fulfilled') setEquity(ec.value.points || []);
      if (tr.status === 'fulfilled') setTrades(tr.value.trades || []);
      if (bp.status === 'fulfilled') setPatterns(bp.value.patterns || []);
      if (td.status === 'fulfilled') setTod(td.value.buckets || []);
      if (bs.status === 'fulfilled') setSessions(bs.value.sessions || []);
      if (dd.status === 'fulfilled') setDrawdown(dd.value);
      if (au.status === 'fulfilled') setAudit(au.value);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => {
    void load();
  }, [load]);

  const overviewCards = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'Win rate', value: `${summary.win_rate.toFixed(1)}%` },
      { label: 'Profit factor', value: summary.profit_factor.toFixed(2) },
      { label: 'Expectancy', value: fmt$(summary.expectancy) },
      { label: 'Total P&L', value: fmt$(summary.total_pnl), className: pnlClass(summary.total_pnl) },
      { label: 'Trades', value: String(summary.total_trades) },
      { label: 'Avg hold', value: `${summary.avg_hold_minutes.toFixed(0)}m` },
    ];
  }, [summary]);

  const handleScrubPreview = async () => {
    setScrubbing(true);
    setError(null);
    try {
      const preview = await reportingApi.scrubLegacyExits({ days: 365, action: 'tag', dry_run: true });
      setScrubPreview(preview);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scrub preview failed');
    } finally {
      setScrubbing(false);
    }
  };

  const handleScrubApply = async () => {
    if (!window.confirm(
      'Tag legacy exit logs as reporting_excluded in Firestore?\n\n' +
      'They will be hidden from /api/reporting P&L. This does not delete documents.'
    )) {
      return;
    }
    setScrubbing(true);
    setError(null);
    try {
      const result = await reportingApi.scrubLegacyExits({ days: 365, action: 'tag', dry_run: false });
      setScrubPreview(result);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scrub failed');
    } finally {
      setScrubbing(false);
    }
  };

  const handlePersist = async () => {
    setPersisting(true);
    try {
      await reportingApi.persistHistory(365);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Persist failed');
    } finally {
      setPersisting(false);
    }
  };

  // Broker-sourced numbers carry source=alpaca; surface a reconciled badge so users
  // trust the figures come from the broker ledger (docs/UNIFIED_REPORTING.md, Phase 4).
  const brokerSource = summary?.source;
  const brokerReconciled = brokerSource === 'alpaca' || brokerSource === 'broker';
  const reconDelta = summary?.reconciliation_delta;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-xl sm:text-2xl font-bold">Reports &amp; Oversight</h1>
            {brokerReconciled ? (
              <span
                title={`Numbers sourced from the broker ledger (Alpaca).${
                  reconDelta != null ? ` Titan-vs-broker delta: $${reconDelta.toFixed(2)}.` : ''
                }`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Reconciled with broker
              </span>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Autonomous-system trajectory, realized P&L &amp; forecast · US/Eastern market days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(periodDays)} onValueChange={(v) => setPeriodDays(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((d) => (
                <SelectItem key={d} value={String(d)}>{d} days</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void handlePersist()} disabled={persisting}>
            {persisting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
            Backfill
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {(['today', 'week', 'month', 'ytd'] as const).map((key) => {
            const m = stats[key];
            const label = key === 'week' ? '7d' : key === 'month' ? '30d' : key.toUpperCase();
            return (
              <Card key={key}>
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-lg font-bold ${pnlClass(m.total_pnl)}`}>
                    {stats.pnl_available === false ? '—' : fmt$(m.total_pnl)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {m.total_trades} {key === 'today' ? 'closed today (ET)' : 'trades'}
                  </p>
                  {key === 'today' && (
                    <p className="text-[10px] text-muted-foreground">$ since prior close (4PM ET)</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="oversight" className="gap-1"><Telescope className="w-3.5 h-3.5" />Oversight</TabsTrigger>
          <TabsTrigger value="overview" className="gap-1"><BarChart3 className="w-3.5 h-3.5" />Overview</TabsTrigger>
          <TabsTrigger value="equity" className="gap-1"><LineChart className="w-3.5 h-3.5" />Equity</TabsTrigger>
          <TabsTrigger value="daily" className="gap-1"><Calendar className="w-3.5 h-3.5" />Daily</TabsTrigger>
          <TabsTrigger value="trades" className="gap-1"><Table2 className="w-3.5 h-3.5" />Trades</TabsTrigger>
          <TabsTrigger value="patterns" className="gap-1"><LayoutGrid className="w-3.5 h-3.5" />Patterns</TabsTrigger>
          <TabsTrigger value="time" className="gap-1"><Clock className="w-3.5 h-3.5" />Time</TabsTrigger>
          <TabsTrigger value="session" className="gap-1"><Clock className="w-3.5 h-3.5" />Session</TabsTrigger>
          <TabsTrigger value="drawdown" className="gap-1"><TrendingDown className="w-3.5 h-3.5" />Drawdown</TabsTrigger>
          <TabsTrigger value="deep" className="gap-1"><Gauge className="w-3.5 h-3.5" />Deep dive</TabsTrigger>
        </TabsList>

        <TabsContent value="oversight" className="mt-4 space-y-4">
          <SystemOversight periodDays={periodDays} />
          <ShadowSoakCard />
        </TabsContent>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {loading && !summary ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {overviewCards.map((c) => (
                <Card key={c.label}>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                    <p className={`text-lg font-bold ${c.className || ''}`}>{c.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {audit && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Exit log scope audit</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground font-mono space-y-1">
                <p>Raw exits (365d): {String(audit.raw_total)}</p>
                <p>After scope filter: {String((audit.scoped as Record<string, number>)?.output)}</p>
                <p>System-managed trades: {String(audit.managed_trade_count)}</p>
                {audit.legacy_cutoff ? <p>Legacy cutoff: {String(audit.legacy_cutoff)}</p> : null}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => void handleScrubPreview()} disabled={scrubbing}>
                    Preview legacy scrub
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => void handleScrubApply()} disabled={scrubbing}>
                    Tag excluded exits
                  </Button>
                </div>
                {scrubPreview && (
                  <p className="pt-2 text-foreground">
                    Scrub: {String(scrubPreview.candidates ?? 0)} candidate(s)
                    {scrubPreview.dry_run === false ? ` · tagged ${String(scrubPreview.tagged ?? 0)}` : ' (preview)'}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="equity" className="mt-4">
          <Card>
            <CardContent className="p-4">
              {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : <MiniEquityChart points={equity} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-auto max-h-[480px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">P&L</TableHead>
                    <TableHead className="text-center">Trades</TableHead>
                    <TableHead className="text-center">Win %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {daily.map((row) => (
                    <TableRow key={String(row.date)}>
                      <TableCell className="font-mono text-xs">{String(row.date)}</TableCell>
                      <TableCell className={`text-right font-semibold ${pnlClass(Number(row.total_pnl || 0))}`}>
                        {fmt$(Number(row.total_pnl || 0))}
                      </TableCell>
                      <TableCell className="text-center">{String(row.total_trades ?? 0)}</TableCell>
                      <TableCell className="text-center">{Number(row.win_rate || 0).toFixed(0)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trades" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-auto max-h-[520px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Pattern</TableHead>
                    <TableHead className="text-right">P&L</TableHead>
                    <TableHead>Exit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trades.map((t, i) => (
                    <TableRow key={String(t.id || i)}>
                      <TableCell className="text-xs font-mono">{String(t.timestamp || '').slice(0, 16)}</TableCell>
                      <TableCell>{String(t.symbol || '')}</TableCell>
                      <TableCell className="text-xs">{String(t.pattern_name || t.pattern_id || '—')}</TableCell>
                      <TableCell className={`text-right font-semibold ${pnlClass(Number(t.realized_pnl || 0))}`}>
                        {fmt$(Number(t.realized_pnl || 0))}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{String(t.exit_reason || '')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pattern</TableHead>
                    <TableHead className="text-center">Trades</TableHead>
                    <TableHead className="text-right">P&L</TableHead>
                    <TableHead className="text-center">WR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patterns.map((p) => (
                    <TableRow key={p.pattern_id}>
                      <TableCell>{p.pattern_name}</TableCell>
                      <TableCell className="text-center">{p.trades ?? (p as PatternMetrics & { total_trades?: number }).total_trades}</TableCell>
                      <TableCell className={`text-right ${pnlClass(p.total_pnl)}`}>{fmt$(p.total_pnl)}</TableCell>
                      <TableCell className="text-center">{p.win_rate.toFixed(0)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-2">
              {tod.map((b) => (
                <div key={b.hour} className="min-w-[72px] rounded border p-2 text-center text-xs">
                  <div className="font-semibold">{b.label}</div>
                  <div className={pnlClass(b.total_pnl)}>{fmt$(b.total_pnl)}</div>
                  <div className="text-muted-foreground">{b.trades ?? 0} tr</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="session" className="mt-4">
          <Card>
            <CardContent className="p-4">
              {loading && !sessions.length ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No session data yet.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SESSION_ORDER.map((key) => {
                    const row = sessions.find((s) => s.session === key);
                    if (!row) return null;
                    const m = row.metrics;
                    const pnl = Number(m.total_pnl || 0);
                    return (
                      <div key={key} className="rounded border p-3 text-center text-xs">
                        <div className="font-semibold">{SESSION_LABELS[key] || key}</div>
                        <div className={`text-lg font-bold ${pnlClass(pnl)}`}>{fmt$(pnl)}</div>
                        <div className="text-muted-foreground">{Number(m.win_rate || 0).toFixed(0)}% WR</div>
                        <div className="text-muted-foreground">{Number(m.total_trades || 0)} trades</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drawdown" className="mt-4">
          {drawdown && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Max DD</p><p className="text-lg font-bold">{fmt$(Number(drawdown.max_drawdown || 0))}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Current DD</p><p className="text-lg font-bold">{fmt$(Number(drawdown.current_drawdown || 0))}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Peak P&L</p><p className="text-lg font-bold">{fmt$(Number(drawdown.peak_equity_pnl || 0))}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Equity P&L</p><p className="text-lg font-bold">{fmt$(Number(drawdown.current_equity_pnl || 0))}</p></CardContent></Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="deep" className="mt-4">
          <PerformanceAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
}
